import { colors } from '../../../theme/tokens/colors';
import { typography } from '../../../theme/tokens/typography';

export function createUserTableOverrides({ semantic, accent } = {}) {
  const table = semantic?.table ?? {};
  const borders = semantic?.borders ?? {};
  const borderColor = table.borderColor ?? borders.border ?? colors.grey[200];

  return {
    accentPrimary: accent?.primary ?? '#2563EB',
    tablePaper: {
      overflow: 'hidden',
      maxWidth: '100%',
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1.5,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      bgcolor: colors.white,
      '& .MuiTableContainer-root': {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      },
      '& [data-pinned="right"]': {
        boxShadow: '-6px 0 10px rgba(15, 23, 42, 0.06)',
      },
      '& [data-pinned="right"]:before': {
        bgcolor: colors.white,
      },
      '& .MuiTableBody-root .MuiTableCell-root': {
        bgcolor: colors.white,
        backgroundColor: colors.white,
      },
      '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
        bgcolor: colors.grey[50],
        backgroundColor: colors.grey[50],
      },
      '& .MuiTableCell-root[data-pinned="true"]': {
        bgcolor: `${colors.white} !important`,
        backgroundColor: `${colors.white} !important`,
        boxShadow: 'none !important',
      },
      '& .MuiTableRow-root:hover .MuiTableCell-root[data-pinned="true"]': {
        bgcolor: `${colors.grey[50]} !important`,
        backgroundColor: `${colors.grey[50]} !important`,
      },
      '& .MuiTableRow-root': {
        height: 44,
      },
    },
    tableContainer: {
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    tableHeadCell: {
      bgcolor: '#F8FAFC',
      color: colors.grey[700],
      fontWeight: 700,
      fontSize: '0.6875rem',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${colors.grey[300]}`,
      py: 0.75,
      px: 1.5,
      '&, & *': {
        color: `${colors.grey[700]} !important`,
      },
      '& .MuiTypography-root': {
        color: `${colors.grey[700]} !important`,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      '& .Mui-TableHeadCell-Content-Labels': {
        color: `${colors.grey[700]} !important`,
      },
      '& .Mui-TableHeadCell-Content-Wrapper': {
        color: `${colors.grey[700]} !important`,
      },
      '& .Mui-TableHeadCell-Content': {
        justifyContent: 'flex-start',
      },
    },
    tableHeadRow: {
      bgcolor: '#F8FAFC',
      boxShadow: 'none',
    },
    tableBodyCell: {
      fontSize: '0.8125rem',
      color: colors.grey[800],
      borderBottom: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
      textAlign: 'left',
      py: 0.5,
      px: 1.5,
      lineHeight: 1.4,
    },
    tableBodyRow: {
      '&:hover td': {
        bgcolor: colors.grey[50],
      },
    },
    tableFooter: {
      root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 1.5,
        py: 0.75,
        minHeight: 44,
        borderTop: `1px solid ${colors.grey[200]}`,
        bgcolor: colors.white,
      },
      summary: {
        color: colors.grey[600],
        fontSize: '0.75rem',
      },
    },
    actionColumnHeadCell: {
      textAlign: 'center',
      px: 1,
      bgcolor: '#F8FAFC',
      '& .Mui-TableHeadCell-Content': {
        justifyContent: 'center',
      },
      '& .Mui-TableHeadCell-Content-Labels': {
        justifyContent: 'center',
      },
    },
    actionColumnBodyCell: {
      textAlign: 'center',
      px: 1,
      py: 0.25,
      bgcolor: colors.white,
      backgroundColor: colors.white,
    },
    rowActionButton: {
      p: 0.375,
      color: colors.grey[600],
      '&:hover': {
        bgcolor: colors.grey[100],
      },
    },
    actionColumnCell: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
  };
}

export function createUserTableFilters({ semantic } = {}) {
  const borders = semantic?.borders ?? {};
  const borderColor = borders.border ?? colors.grey[200];
  const surface = semantic?.surfaces?.surface ?? colors.white;

  return {
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'end',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      gap: 1.5,
      width: '100%',
      mb: 1.5,
      '& input::selection': {
        backgroundColor: 'transparent',
      }
    },
    search: {
      width: { xs: '100%', sm: 320 },
      maxWidth: { lg: 290, md:150 },
      flexShrink: 0,
      '& > div:last-of-type': {
        height:'40px',
        bgcolor: surface,
        borderColor,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:focus-within': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
    statusFilter: {
      width: { xs: '100%', sm: 200 },
      maxWidth: { lg: 220, md:150 },
      flexShrink: 0,
      '& .MuiAutocomplete-root': {
        width: '100%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        top: 0,
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: surface,
        borderRadius: '8px',
        fontSize: typography.fontSizeSm,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '& fieldset': {
          borderColor,
        },
        '&:hover fieldset': {
          borderColor: colors.grey[400],
        },
        '&.Mui-focused fieldset': {
          borderColor: semantic?.interactive?.primary ?? '#2563EB',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
    profileFilter: {
      width: { xs: '100%', sm: 280 },
      maxWidth: { lg: 300, md:150 },
      flexShrink: 0,
      '& .MuiAutocomplete-root': {
        width: '100%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        top: 0,
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: surface,
        borderRadius: '8px',
        fontSize: typography.fontSizeSm,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '& fieldset': {
          borderColor,
        },
        '&:hover fieldset': {
          borderColor: colors.grey[400],
        },
        '&.Mui-focused fieldset': {
          borderColor: semantic?.interactive?.primary ?? '#2563EB',
          borderWidth: '1px',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
    orgFilter: {
      width: { xs: '100%', sm: 280 },
      maxWidth: { lg: 300, md:175},
      flexShrink: 0,
      '& .MuiAutocomplete-root': {
        width: '100%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        top: 0,
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: surface,
        borderRadius: '8px',
        fontSize: typography.fontSizeSm,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '& fieldset': {
          borderColor,
        },
        '&:hover fieldset': {
          borderColor: colors.grey[400],
        },
        '&.Mui-focused fieldset': {
          borderColor: semantic?.interactive?.primary ?? '#2563EB',
          borderWidth: '1px',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)',
        },
      },
    },
  };
}

export function createUserPageSx({ semantic } = {}) {
  return {
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      minHeight: 0,
      px: { xs: 2, md: 3 },
      py: 2.5,
      bgcolor: semantic?.surfaces?.surfaceSubtle ?? '#F8FAFC',
    },
  };
}
